import { Toast } from 'antd-mobile';
import { observable, action, flow, computed } from "mobx";
import { IProjectItem, ITaskItem, IMembersSel, IMemberNew } from "../../entity/company/project";
import { ajax } from "@clouderwork/ywk";
import { initData } from "@src/pages/Company/ProjectList";
import { memberItem2Memberssel, memberNew2MembersSel } from '@src/utils/mapping';
// import { findExpires } from "./model";
/**
 * @module:  项目数据
 * @author: Liang Huang
 * @date: 2019-10-16 14:37:27
 */

interface ISelect {
  mode: number; // 查询的类型 1-我负责和参与的项目 2-我关注的项目 3-我创建的项目 4-我下属的项目 不传默认1
  keyword: string; // 关键字
  creator: string; // 项目创建子uuid
  manager: string; // 项目负责人uuid
  department_uuid?: string; // 下属的项目 部门uuid 逗号分隔
  member_uuid?: string; // 下属的项目，成员的uuid 逗号分隔
  sort?: string; // 列表排序方式1-最近使用2-创建时间, 3截止时间,4-延期率,5-完成度 默认不传则为 1
}

export interface IProject {
  having?: IProjectItem[];
  finish?: IProjectItem[];
  all?: IProjectItem[];
}

export interface IMembers {
  company_user_id: number;
  completed: number;
  contact: string;
  d_name: string;
  department_uuid: string;
  email: string;
  evaluation: string;
  expired: number;
  expired_task_rate: string;
  is_superadmin: boolean;
  is_withdraw: number;
  job_number: string;
  logo: string;
  manager: boolean;
  manager_group: any[];
  manager_groups: string; // 是否有下属
  role_id: number;
  role_name: string;
  scope_name: string;
  slug_user_name: string;
  status: string;
  unfinished: number;
  user_id: number;
  user_name: string;
  user_type: string;
  user_uuid: string;
  uuid: string;
  wait_join: number;
  work_status: string;
  work_status_txt: string;
  [propName: string]: any
}

interface IDetailSelect {
  category: number | null;                    // 1 | 2 | 3 | 4 // 看板类别
  // ftype: 'all' | 'e' | 'f' | 'c' | '' // 全部任务；指派给我的；我关注的；我创建的
  status: number;                   // status 1-本周代办, 2-今日到期, 3-延期未完成, 4-未完成 5-已完成, 6-关注的, 不传是全部
  executor_id: string;              // 筛选人的uuid, 与我相关传自己的uuid
}

let minCategory: number = 1;

export default class Project {
  @observable projects: IProjectItem[] = [];
  @observable projectsMap: {
    [propName: string]: IProjectItem
  } = {};
  // 总条数
  @observable projectCount: Number = 0;
  /**
   * 搜索关键字
   */
  @observable keyword: string = "";

  // list列表页交互状态
  @observable selects: ISelect = {
    mode: 1,
    keyword: "",
    creator: "",
    manager: ""
  };

  @observable members: IMembersSel[] = [];
  @observable projectsMembers: IMembersSel[] = [];
  @observable ajaxStatus = "init";

  @observable kanbanStatus = 'init';
  @observable setttingStatus = 'init';
  @observable expireStatus = 'init';
  @observable fileStatus = 'init';

  // 归档或删除操作
  @observable actionStatus = 'init';

  // 打开项目沟通群状态
  @observable jumpStatus = 'init';
  @observable detailSelects: IDetailSelect = {
    category: null,
    status: 2,
    executor_id: ''
  };

  @observable kanbanIndex: number = 0;
  @observable isManager: boolean = false;

  @action.bound
  countByType(type: string, data: IProjectItem) {
    if (type === 'finish') {
      return data.done_count;
    } else if (type === 'having') {
      return data.all_task - data.done_count;
    } else if (type === 'all') {
      return data.all_task;
    }
    return data.all_task - data.done_count;
  }
  /**
   * 获取项目列表数据
   */
  @action.bound
  getProjects = flow(function* (this: Project, params: ISelect = this.selects) {
    this.ajaxStatus = 'pending'
    const _params = { ...params, sort: 2 };
    // _params.pstatus_group = _params.pstatus_group === 'all' ? '' : _params.pstatus_group;

    try {
      const res = yield ajax.getJson("/company/api/projects", _params);
      this.ajaxStatus = "done";
      if (+res.error_code === 0) {
        let projects = res.result;
        this.projects = projects
        this.projectCount = res.total;
        this.updateMaps(this.projects);
      }
      return res;
    } catch (error) {
      this.ajaxStatus = "error";
      return error;
    }
  });

  /**
   * 获取管理员成员信息
   */
  @action.bound
  getUsers = flow(function* (this: Project, uuid: string) {
    this.ajaxStatus = "pending";
    try {
      const res = yield ajax.getJson(`/company/api/project/members/manage-scopes`);
      this.ajaxStatus = "done";
      if (+res.error_code === 0) {
        this.members = memberNew2MembersSel(res.result) as IMembersSel[];
      }
      return res;
    } catch (err) {
      this.ajaxStatus = "error";
      return err;
    }
  });

  /**
   * 获取项目信息
   */
  @action.bound
  getProjectInfo = flow(function* (this: Project, uuid: string) {
    this.ajaxStatus = 'pending';
    try {
      const res = yield ajax.getJson(`/company/api/projects/${uuid}`);
      this.ajaxStatus = 'done';
      if (+res.error_code === 0) {
        this.updateMaps([res.result]);
        // 初始化时，默认选中第一个
        if(!this.detailSelects.category) {
          minCategory = res.result.project_category ? Math.min(...res.result.project_category): 1;
          this.detailSelects.category = minCategory;
        }
      }
      return res;
    } catch (error) {
      this.ajaxStatus = 'error';
      return error;
    }
  })

  /**
   * 获取当前分类任务信息
   * @param slug
   * @param category
   * @ftype 筛选类型
   */
  @action.bound
  getTaskInfo = flow(function* (this: Project, isSearch: boolean = false, uuid: string, category: number, status?: number, executor_id?: string) {
    this.kanbanStatus = 'pending';
    try {
      const url = !isSearch ? `/company/api/tasks/kanban/${uuid}` : `/company/api/tasks/project/search`;
      let params;
      if (!isSearch) {
        params = {
          category
        }
      } else {
        params = {
          slug: uuid,
          executor_id,
          status,
          category,
        }
      }
      const res = yield ajax.getJson(url, params);
      this.kanbanStatus = 'done';
      if (+res.error_code === 0) {
        this.updateMapItem(this.projectsMap[uuid], 'tasks', res.result.result || res.result.tasks);
      }
      return res;
    } catch (error) {
      this.kanbanStatus = 'error';
      return error;
    }
  })

  /**
   * 获取当前项目看板信息（有哪些任务分类）
   */
  @action.bound
  getKanbans = flow(function* (this: Project, uuid: string, category: number) {
    this.kanbanStatus = 'pending';
    try {
      const res = yield ajax.getJson(`/company/api/kanbans/project/${uuid}`, { category });
      this.kanbanStatus = 'done';
      if (+res.error_code === 0) {
        this.updateMapItem(this.projectsMap[uuid], 'kanbans', res.result);
      }
      return res;
    } catch (error) {
      this.kanbanStatus = 'error';
      return error;
    }
  })

  /**
   * 删除项目成员
   */
  @action.bound
  deleteMember = flow(function* (this: Project, projectUuid: string, user_id: string) {
    try {
      const res: any = yield ajax.deleteJson(`/company/api/project/members/${projectUuid}`, { user_uuid: user_id });
      if (+res.error_code === 0) {
        const project = this.projectsMap[projectUuid];
        if (!project) return;
        const members = project.members ? project.members.filter((m) => m.id !== user_id) : [];
        this.projectsMembers = memberItem2Memberssel(members) as IMembersSel[];
        this.updateMapItem(this.projectsMap[projectUuid], 'members', members);
      } else {
        Toast.fail(res.msg);
      }
    } catch (error) {
      return error;
    }
  })

  /**
   * 获取项目成员
   */
  @action.bound
  getMembers = flow(function* (this: Project, projectUuid: string, keyword?: string) {
    this.setttingStatus = 'pending';
    try {
      const res = yield ajax.getJson(`/company/api/project/members/${projectUuid}`);
      this.setttingStatus = 'done';
      if (+res.error_code === 0) {
        this.projectsMembers = memberItem2Memberssel(res.result) as IMembersSel[];
        this.updateMapItem(this.projectsMap[projectUuid], 'members', res.result);
      }
      return res;
    } catch (error) {
      this.setttingStatus = 'error';
      return error;
    }
  })
  /**
   * 获取项目延期人员列表
   */
  @action.bound
  getExpireList = flow(function* (this: Project, project_id: number, projectUuid: string) {
    this.expireStatus = 'pending';
    try {
      const res = yield ajax.getJson('/company/api/tasks/app/delay', { project_id });
      this.expireStatus = 'done';
      if (+res.error_code === 0) {
        res.result = res.result.filter(r => r.total_count && r.total_count > 0);
        this.updateMapItem(this.projectsMap[projectUuid], 'expires', res.result);
      }
      return res;
    } catch (error) {
      this.expireStatus = 'error';
      return error;
    }
  })
  /**
   * 归档项目
   */
  @action.bound
  archiveProject = flow(function* (this: Project, projectUuid: string) {
    this.actionStatus = 'pending';
    try {
      const res = yield ajax.postJson(`/company/api/projects/archive/${projectUuid}`);
      this.actionStatus = 'done';
      return res;
    } catch (error) {
      this.actionStatus = 'error';
      return error;
    }
  })
  /**
   * 删除项目
   */
  @action.bound
  deleteProject = flow(function* (this: Project, projectUuid: string) {
    this.actionStatus = 'pending';
    try {
      const res = yield ajax.deleteJson(`/company/api/projects/${projectUuid}`);
      this.actionStatus = 'done';
      return res;
    } catch (error) {
      this.actionStatus = 'error';
      return error;
    }
  })
  /**
   * 获取项目附件
   */
  // @action.bound
  // getProjectFiles = flow(function* (this: Project, slug: string) {
  //   this.fileStatus = 'pending';
  //   try {
  //     const res = yield ajax.getJson('/company/api/projects', { slug });
  //     if (+res.error_code === 0) {
  //       if (res.projects && res.projects.length > 0) {
  //         this.updateMapItem(this.projectsMap[slug], 'files', res.projects[0].attachments);
  //       }
  //     }
  //     this.fileStatus = 'done';
  //     return res;
  //   } catch (error) {
  //     this.fileStatus = 'error';
  //     return error;
  //   }
  // })
  /**
   * 获取conversation Info
   */
  @action.bound
  getConversationInfo = flow(function* (this: Project, usergroup_id: string, project_id: string, from_usergroup_id: string, platform: number = 1, from: number = 4) {
    this.jumpStatus = 'pending';
    try {
      const res = yield ajax.getJson('/wokexin/api/v1/conversation.info.messagelist', {
        usergroup_id,
        device_id: '',
        platform,
        from_usergroup_id,
        project_id,
        from
      });
      this.jumpStatus = 'done';
      return res;
    } catch (error) {
      this.jumpStatus = 'error';
      return error;
    }
  })
  /**
   * 编辑项目成员
   * @param user_id 字符串 逗号分割
   * @param slug
  */
  @action.bound
  editProjectMembers = flow(function* (this: Project, project_uuid: string, user_uuid: string[]) {
    try {
      return yield ajax.post('/company/api/project/members', { project_uuid, user_uuid });
    } catch (error) {
      return error;
    }
  });
  /**
   * 当前成员对此项目是否有操作权限
   * @param project_uuid 字符串 逗号分割
  */
  @action.bound
  getHasAction = flow(function* (this: Project, project_uuid: string) {
    try {
      const res = yield ajax.getJson(`/company/api/project/members/manage/${project_uuid}`);
      if (+res.error_code === 0) {
        this.isManager = res.result.mtype === 1;
      }
      return res;
    } catch (error) {
      return error;
    }
  });

  /**
   * 更新当前项目列表
   * @param project 项目
   * @param tasks 任务列表
   */
  @action.bound
  updateMapItem(project: IProjectItem, key: string, dataSource: any) {
    project[key] = dataSource;
  }

  /**
   * 更新project缓存map
   * @param projects
   */
  @action.bound
  updateMaps(projects: IProjectItem[]) {
    for (const item of projects) {
      if (!this.projectsMap[item.uuid]) {
        // 初始化
        this.projectsMap[item.uuid] = item;
      } else {
        // 更新
        this.projectsMap[item.uuid] = {
          ...this.projectsMap[item.uuid],
          ...item
        }
      }
    }
  }

  @action.bound
  updateCurList(keyword: string) {
    // this.keyword = val;
    if (!keyword) return [];
    return this.projects.filter(project => {
      return project.name.includes(keyword);
    });
  }

  // 重置列表页数据
  @action.bound
  resetPageListData() {
    this.selects = {
      mode: 1,
      creator: "",
      manager: "",
      keyword: ""
    };
  }

  @computed get menuIndex() {
    return initData.findIndex(v => +v.value === this.selects.mode);
  }

  /**
   * 重置项目详情页筛选数据
   */
  @action.bound
  resetFilter() {
    this.kanbanIndex = 0;
    this.detailSelects.executor_id = '';
    this.detailSelects.status = 0;
    this.detailSelects.category = minCategory;
  }
}
